import React, { useCallback, useEffect, useRef, useState } from "react";
import LocalizedLink from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { trackEvent } from "@/utils/analytics";
import { PROJECTS } from "@/data/projects";
import DeviceComposite from "@/components/DeviceComposite";
import "./PortfolioSection.scss";

/** Auto-play advances this often while the section is in view and idle. */
export const AUTOPLAY_MS = 4000;
/** Desktop hover intent: a spine opens after the pointer rests this long. */
export const HOVER_INTENT_MS = 120;

const DESKTOP_HOVER_QUERY = "(min-width: 1024px) and (hover: hover)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const matches = (query: string) =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(query).matches;

const pad = (n: number) => String(n).padStart(2, "0");

const ExternalIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M7 3H3v10h10V9M10 2h4v4M14 2L8 8" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

type SelectVia = "click" | "key" | "focus" | "hover";

/**
 * "Spines" portfolio browser. Desktop (≥1024px): a row of vertical spines on
 * each product's brand color; the active one grows to show the live product in
 * a browser + phone frame. Narrow screens: the same items as stacked bands.
 * One DOM for both layouts — CSS switches the presentation.
 */
const PortfolioSection: React.FC = () => {
  const { t } = useTranslation();
  const { ref: revealRef, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const [active, setActive] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(() => matches(REDUCED_MOTION_QUERY));
  // Auto-play runs until the visitor picks a project (or presses pause).
  const [auto, setAuto] = useState(() => !matches(REDUCED_MOTION_QUERY));
  const [inView, setInView] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState !== "hidden"
  );

  const listRef = useRef<HTMLUListElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const select = useCallback((i: number, via: SelectVia) => {
    setActive(i);
    setAuto(false);
    // Explicit picks only — focus follows clicks/keys and hover is too noisy.
    if (via === "click" || via === "key") {
      trackEvent("portfolio_select", { project: PROJECTS[i].key, via });
    }
  }, []);

  // prefers-reduced-motion: no auto-play, no transitions (CSS handles the rest).
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => {
      setReducedMotion(mq.matches);
      if (mq.matches) setAuto(false);
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Only advance while the spines are actually on screen.
  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.35,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onVisibility = () => setPageVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const playing = auto && !reducedMotion && inView && pageVisible && !hovered && !focused;

  useEffect(() => {
    if (!playing) return;
    const id = setTimeout(() => setActive((a) => (a + 1) % PROJECTS.length), AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [playing, active]);

  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    },
    []
  );

  const cancelHoverIntent = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  };

  const onSpineEnter = (i: number) => {
    cancelHoverIntent();
    if (i === active || !matches(DESKTOP_HOVER_QUERY)) return;
    hoverTimer.current = setTimeout(() => select(i, "hover"), HOVER_INTENT_MS);
  };

  const focusSpine = (i: number) => {
    const n = PROJECTS.length;
    const next = ((i % n) + n) % n;
    buttonRefs.current[next]?.focus();
    select(next, "key");
  };

  const onSpineKeyDown = (e: React.KeyboardEvent, i: number) => {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        focusSpine(i + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        focusSpine(i - 1);
        break;
      case "Home":
        focusSpine(0);
        break;
      case "End":
        focusSpine(PROJECTS.length - 1);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  const current = PROJECTS[active];

  return (
    <section className="portfolio-section">
      <div id="portfolio-section" className="container" ref={revealRef}>
        <div className={`sec-head row reveal${isVisible ? " in" : ""}`}>
          <div>
            <span className="sec-label">{t("portfolio.label")}</span>
            <h2 className="sec-title">
              {(() => {
                const title = t("portfolio.title");
                const hl = t("portfolio.titleHighlight");
                const [before, after = ""] = title.split(hl);
                return <>{before}<em>{hl}</em>{after}</>;
              })()}
            </h2>
          </div>
          {!reducedMotion && (
            <button
              type="button"
              className="port-autoplay"
              onClick={() => setAuto((a) => !a)}
            >
              <span aria-hidden="true">{auto ? "❚❚" : "▶"}</span>
              {auto ? t("portfolio.autoplayPause") : t("portfolio.autoplayPlay")}
            </button>
          )}
        </div>

        <ul
          className="port-spines"
          ref={listRef}
          aria-label={t("portfolio.listAriaLabel")}
          style={{ "--n": PROJECTS.length } as React.CSSProperties}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => {
            setHovered(false);
            cancelHoverIntent();
          }}
          // Any press inside the browser ends auto-play for good, like the personal site.
          onPointerDown={() => setAuto(false)}
          onKeyDown={() => setAuto(false)}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
          }}
        >
          {PROJECTS.map((p, i) => {
            const on = i === active;
            const panelId = `port-panel-${p.slug}`;
            const nameId = `port-name-${p.slug}`;
            const short = t(`portfolio.${p.key}.short`);
            return (
              <li
                key={p.key}
                className={`port-spine${on ? " is-active" : ""}`}
                style={
                  {
                    "--brand": p.brand.bg,
                    "--brand-ink": p.brand.ink,
                  } as React.CSSProperties
                }
                onMouseEnter={() => onSpineEnter(i)}
                onMouseLeave={cancelHoverIntent}
              >
                <button
                  type="button"
                  className="port-spine-btn"
                  ref={(el) => {
                    buttonRefs.current[i] = el;
                  }}
                  aria-expanded={on}
                  aria-controls={panelId}
                  aria-label={t("portfolio.spineAriaLabel", { name: p.name, short })}
                  onClick={() => select(i, "click")}
                  onFocus={() => {
                    if (!on) select(i, "focus");
                  }}
                  onKeyDown={(e) => onSpineKeyDown(e, i)}
                />

                {/* Collapsed face — purely visual; the button above names it. */}
                <div className="port-spine-face" aria-hidden="true">
                  <img
                    className="port-spine-texture"
                    src={p.spine.webp2x}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="port-spine-num">{pad(i + 1)}</span>
                  <span className="port-spine-name">{p.name}</span>
                  <span className="port-spine-short">{short}</span>
                  <span className="port-spine-peek">
                    <img src={p.screens.mobile.webp2x} alt="" loading="lazy" decoding="async" />
                  </span>
                  {p.featured && (
                    <span className="port-spine-pill">{t("portfolio.featuredPill")}</span>
                  )}
                </div>

                {/* Expanded panel */}
                <div
                  id={panelId}
                  className="port-panel"
                  role="region"
                  aria-labelledby={nameId}
                  aria-hidden={!on}
                >
                  <div className="port-panel-inner">
                    <span className="port-panel-num" aria-hidden="true">{pad(i + 1)}</span>
                    {/* Mouse/touch shortcut to the case study; the CTA below is
                        the accessible link, so this one is hidden from AT. */}
                    <LocalizedLink
                      to={`/work/${p.slug}`}
                      className="port-panel-visual"
                      tabIndex={-1}
                      aria-hidden="true"
                      onClick={() => trackEvent("case_study_open", { project: p.key })}
                    >
                      <DeviceComposite
                        project={p}
                        variant="panel"
                        desktopSizes="(min-width: 1024px) 440px, 300px"
                      />
                    </LocalizedLink>
                    <div className="port-panel-text">
                      <div className="port-panel-head">
                        <h3 className="port-panel-name" id={nameId}>{p.name}</h3>
                        {p.featured && (
                          <span className="port-panel-pill">{t("portfolio.featuredPill")}</span>
                        )}
                      </div>
                      <ul className="port-panel-tags">
                        {p.tags.map((tag) => (
                          <li key={tag}>{tag}</li>
                        ))}
                      </ul>
                      <p className="port-panel-tagline">{t(`caseStudies.${p.slug}.tagline`)}</p>
                      <div className="port-panel-actions">
                        <LocalizedLink
                          to={`/work/${p.slug}`}
                          className="port-panel-cta"
                          tabIndex={on ? undefined : -1}
                          onClick={() => trackEvent("case_study_open", { project: p.key })}
                        >
                          {t("portfolio.readCaseStudy")} <ArrowIcon />
                        </LocalizedLink>
                        <a
                          href={p.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="port-panel-live"
                          tabIndex={on ? undefined : -1}
                          aria-label={`${t("portfolio.visitLiveSite")}: ${p.name}`}
                          onClick={() => trackEvent("portfolio_open", { project: p.key })}
                        >
                          {t("portfolio.visitLiveSite")} <ExternalIcon />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="port-meta">
          <span className="port-count">
            {pad(active + 1)} / {pad(PROJECTS.length)}
          </span>
          <p className="port-desc" key={current.key}>
            {t(`portfolio.${current.key}.description`)}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
